import apiClient from '@shared/services/http/apiClient';
import { forcedLogout } from './authCleanup';
import { getUserEncrypted } from './storageUtils';

// the modal stays open until the user picks "Continuar" or "Salir" — or
// the session naturally expires.
let isPreExpiryPopupOpen = false;

export const setPreExpiryPopupOpen = (open: boolean): void => {
  isPreExpiryPopupOpen = open;
};

export const isPreExpiryPopupActive = (): boolean => isPreExpiryPopupOpen;

interface SessionStatus {
  expiresAt: number;
  [key: string]: any;
}

export const getSessionStatus = (): SessionStatus | null => {
  try {
    const cookies = document.cookie.split('; ');

    // Canonical: backend's setSessionCookies writes session_e (epoch ms of expiry).
    const sessionEndRaw = cookies.find((row) => row.startsWith('session_e='))?.split('=')[1];
    if (sessionEndRaw) {
      const expiresAt = Number(decodeURIComponent(sessionEndRaw));
      if (Number.isFinite(expiresAt)) return { expiresAt };
    }

    // Fallback: older backends emit session_status as a JSON blob.
    const sessionStatus = cookies.find((row) => row.startsWith('session_status='))?.split('=')[1];
    return sessionStatus ? JSON.parse(decodeURIComponent(sessionStatus)) : null;
  } catch (error) {
    return null;
  }
};

export const isSessionExpired = (): boolean => {
  const expiresAt = getSessionStatus()?.expiresAt;
  if (!expiresAt) return false;
  return Date.now() >= expiresAt;
};

export const shouldRenewSession = (): boolean => {
  const expiresAt = getSessionStatus()?.expiresAt;
  if (!expiresAt) return false;
  const timeLeft = expiresAt - Date.now();
  return timeLeft > 0 && timeLeft < 300000; // 5 minutes
};

export type RenewTokenResult = {
  success: boolean;
  error?: string;
  code?: string;
};

const getRenewErrorMessage = (error: unknown): string => {
  const responseData = (error as { response?: { data?: { message?: string; errors?: Array<{ message?: string }> } } })
    ?.response?.data;
  const fromErrors = responseData?.errors?.[0]?.message;
  if (typeof fromErrors === 'string' && fromErrors.trim()) return fromErrors;
  if (typeof responseData?.message === 'string' && responseData.message.trim()) return responseData.message;
  if (error instanceof Error && error.message.trim()) return error.message;
  return 'No se pudo renovar la sesión. Intenta de nuevo.';
};

let renewalPromise: Promise<RenewTokenResult> | null = null;
let lastSuccessfulRenewAt = 0;
const RECENT_RENEW_WINDOW_MS = 20_000;

export const wasSessionRenewedRecently = (windowMs = RECENT_RENEW_WINDOW_MS): boolean =>
  Date.now() - lastSuccessfulRenewAt < windowMs;

const readRenewFailure = (data: {
  message?: string;
  errors?: Array<{ message?: string }>;
} | undefined): string => {
  const fromErrors = data?.errors?.[0]?.message;
  if (typeof fromErrors === 'string' && fromErrors.trim()) return fromErrors;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  return 'La renovación de sesión no fue exitosa.';
};

export const renewToken = async (): Promise<RenewTokenResult> => {
  if (renewalPromise) return renewalPromise;

  renewalPromise = (async () => {
    try {
      const response = await apiClient.post('/api/v2/corresponsal/renew-session', {});
      if (response?.data?.success === true) {
        lastSuccessfulRenewAt = Date.now();
        return { success: true };
      }
      const code = response?.data?.errors?.[0]?.code;
      return {
        success: false,
        error: readRenewFailure(response?.data),
        ...(typeof code === 'string' && code.trim() ? { code } : {}),
      };
    } catch (error) {
      // 401s are handled by the apiClient response interceptor.
      // Network/timeout errors are non-fatal here (see note above).
      return { success: false, error: getRenewErrorMessage(error) };
    } finally {
      renewalPromise = null;
    }
  })();

  return renewalPromise;
};

const NON_RENEWABLE_URL_PARTS = ['renew-session', '/login', '/validate-user', '/logout/'];
const isAuthFlowUrl = (url: string): boolean => NON_RENEWABLE_URL_PARTS.some((part) => url.includes(part));

const hasLocalUser = (): boolean => {
  try {
    return Boolean(localStorage.getItem('USER'));
  } catch {
    return false;
  }
};

const leaveIfSessionRemoved = (): boolean => {
  if (hasLocalUser()) return false;
  forcedLogout('unknown');
  return true;
};

let signedOutListenerAttached = false;

const watchOtherTabLogout = (): void => {
  if (signedOutListenerAttached || typeof window === 'undefined') return;
  signedOutListenerAttached = true;
  window.addEventListener('storage', (event) => {
    if (event.storageArea !== localStorage) return;
    if (event.key !== null && event.key !== 'USER') return;
    if (event.newValue) return;
    leaveIfSessionRemoved();
  });
};

export const setupSessionExpirationCheck = (): void => {
  const user = getUserEncrypted();
  if (!user) return;

  watchOtherTabLogout();

  apiClient.interceptors.request.use(async (config) => {
    if (leaveIfSessionRemoved()) {
      return Promise.reject(new Error('Session closed'));
    }
    if (isSessionExpired()) {
      forcedLogout('expired');
      return Promise.reject(new Error('Session expired'));
    }

    const url = config.url ?? '';
    if (
      shouldRenewSession() &&
      !wasSessionRenewedRecently() &&
      !isPreExpiryPopupOpen &&
      !isAuthFlowUrl(url)
    ) {
      await renewToken();
    }

    return config;
  });

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args: any[]) {
    checkSessionOnRouteChange();
    return originalPushState.apply(this, args as any);
  };

  history.replaceState = function (...args: any[]) {
    checkSessionOnRouteChange();
    return originalReplaceState.apply(this, args as any);
  };
};

const checkSessionOnRouteChange = (): void => {
  if (leaveIfSessionRemoved()) return;
  if (isSessionExpired()) {
    forcedLogout('expired');
    return;
  }

  if (shouldRenewSession() && !wasSessionRenewedRecently() && !isPreExpiryPopupOpen) {
    void renewToken();
  }
};
